import { documentMarkerPattern } from './documentMarker';

export interface ViewerHighlightContext {
  query: string;
  answer: string;
}

export type ViewerSourceTarget =
  | { kind: 'ris'; url: string }
  | { kind: 'document'; documentId: string; externalUrl: string | null }
  | { kind: 'external'; url: string }
  | { kind: 'none' };

export interface ViewerOpenPort {
  openViewer: (url: string, query: string, context?: string) => Promise<void>;
  openDocumentViewer: (documentId: string, query: string, context?: string) => Promise<void>;
}

const RIS_HOSTS = new Set(['ris.bka.gv.at', 'www.ris.bka.gv.at']);
/**
 * Die Abrufschnittstelle des RIS liefert Dokumentadressen auf einem eigenen
 * Host. Sie zeigen auf dasselbe Dokument wie die amtliche Adresse und werden
 * darauf zurückgeführt; andere Pfade dieses Hosts gelten nicht als
 * RIS-Adresse.
 */
const RIS_DOCUMENT_MIRROR_HOSTS = new Set(['ogd.ris.bka.gv.at']);
const RIS_OFFICIAL_HOST = 'www.ris.bka.gv.at';
const RIS_DOCUMENT_PATH = /^\/Dokumente\//i;
const DOCUMENT_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DOCUMENT_ID_IN_TEXT = /(?:documentId=|dokumentId:\s*)([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;
/** Die gemeinsame Marke (siehe documentMarker.ts). */
const DOCUMENT_MARKER = documentMarkerPattern();
/**
 * Dokumentkennungen, die die Antwort ohne Marke nennt: "Dokumenten-ID: UUID",
 * "WKO Dokumenten-ID: UUID" oder ein blankes "dokumentId: UUID". Die bisherige
 * Chat-Formatierung kannte diese beiden Rückfallformen; die Markdown-Darstellung
 * kennt nur die Marke, deshalb werden sie zur Marke gemacht.
 *
 * Das Komma davor und der Leerraum daneben werden mitgenommen, damit die Marke
 * sauber an ihre Stelle tritt. Der Leerraum ist dabei begrenzt: unbegrenzt und
 * am Anfang des Ausdrucks stehend, liefe er auf einer langen Leerraumkette von
 * jeder Stelle aus erneut durch und hielte die Darstellung minutenlang an.
 */
const DOCUMENT_ID_WITHOUT_MARKER =
  /[ \t]{0,8}(?:,[ \t]{0,8})?(?:(?:WKO[- ])?Dokumenten-ID:?|dokumentId:)[ \t]*([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/gi;
/**
 * Blanke Adresse im Fließtext. Die Schutzmarke der bereits gesetzten
 * Verweise gehört nie dazu: stünde eine blanke Adresse unmittelbar an
 * einem geschützten Verweis, verschlänge die Adresse sonst die Marke,
 * trüge sie verstümmelt in ihrem Ziel und der geschützte Verweis ginge
 * verloren.
 */
const HTTP_URL = /https?:\/\/[^\s<>"'\u0000]+/gi;
const MARKDOWN_LINK = /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/g;

export function safeHttpUrl(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  try {
    const parsed = new URL(value.trim());
    if (
      (parsed.protocol !== 'http:' && parsed.protocol !== 'https:')
      || parsed.username !== ''
      || parsed.password !== ''
      || parsed.port !== ''
    ) return null;
    return parsed.href;
  } catch {
    return null;
  }
}

/**
 * Prüft, ob eine Adresse auf die eigene API zeigt. Die Adresse wird gegen die
 * eigene Herkunft aufgelöst, weil Schreibweisen wie "/\\example.org/x.pdf" im
 * Browser auf eine fremde Herkunft zeigen, eine reine Präfixprüfung sie aber
 * durchlässt. Ohne bekannte Herkunft gilt die Adresse als unsicher.
 */
export function isSafeApiPath(value: unknown, origin?: string): boolean {
  if (typeof value !== 'string' || value.length === 0) return false;
  const base = origin ?? (typeof window !== 'undefined' ? window.location.origin : null);
  if (!base) return false;
  try {
    const resolved = new URL(value, base);
    return resolved.origin === new URL(base).origin && resolved.pathname.startsWith('/api/');
  } catch {
    return false;
  }
}

export function normalizeOfficialRisUrl(value: unknown): string | null {
  const safe = safeHttpUrl(value);
  if (!safe) return null;
  const parsed = new URL(safe);
  if (parsed.protocol !== 'https:') return null;
  const host = parsed.hostname.toLowerCase();
  const isDocumentPath = RIS_DOCUMENT_PATH.test(parsed.pathname);
  if (!RIS_HOSTS.has(host)) {
    if (!isDocumentPath || !RIS_DOCUMENT_MIRROR_HOSTS.has(host)) return null;
    parsed.hostname = RIS_OFFICIAL_HOST;
  }
  parsed.hash = '';
  if (isDocumentPath && /\.xml$/i.test(parsed.pathname)) {
    parsed.pathname = parsed.pathname.replace(/\.xml$/i, '.html');
  }
  return parsed.href;
}

export function isOfficialRisUrl(value: unknown): boolean {
  return normalizeOfficialRisUrl(value) !== null;
}

function urlsIn(value: unknown): string[] {
  if (typeof value !== 'string') return [];
  const direct = safeHttpUrl(value);
  if (direct) return [direct];
  return Array.from(value.matchAll(HTTP_URL), match => match[0].replace(/[\]),.;!?]+$/g, ''))
    .map(safeHttpUrl)
    .filter((item): item is string => item !== null);
}

function recordOf(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

export function returnedDocumentId(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (DOCUMENT_UUID.test(trimmed)) return trimmed;
  return DOCUMENT_ID_IN_TEXT.exec(trimmed)?.[1] ?? null;
}

export function resolveViewerSourceTarget(source: unknown): ViewerSourceTarget {
  const record = recordOf(source);
  const identifiers = recordOf(record?.identifiers);
  const candidates = record
    ? [record.url, record.risLink, record.sourceUrl, ...(Array.isArray(record.urls) ? record.urls : [])]
    : [source];
  const urls = candidates.flatMap(urlsIn);
  const risUrl = urls.map(normalizeOfficialRisUrl).find((item): item is string => item !== null);
  if (risUrl) return { kind: 'ris', url: risUrl };

  const documentId = [
    record?.documentId,
    record?.dokumentId,
    identifiers?.documentId,
    identifiers?.dokumentId,
    ...candidates,
  ].map(returnedDocumentId).find((item): item is string => item !== null);
  const externalUrl = urls.find(url => normalizeOfficialRisUrl(url) === null) ?? null;
  if (documentId) return { kind: 'document', documentId, externalUrl };
  if (externalUrl) return { kind: 'external', url: externalUrl };
  return { kind: 'none' };
}

export async function openViewerSource(
  target: ViewerSourceTarget,
  highlight: ViewerHighlightContext,
  viewer: ViewerOpenPort,
): Promise<void> {
  if (target.kind === 'ris') {
    await viewer.openViewer(target.url, highlight.query, highlight.answer);
  } else if (target.kind === 'document') {
    await viewer.openDocumentViewer(target.documentId, highlight.query, highlight.answer);
  }
}

export function risReferenceLabel(url: string): string {
  const normalized = normalizeOfficialRisUrl(url);
  if (!normalized) return 'RIS';
  const parsed = new URL(normalized);
  const documentMatch = parsed.pathname.match(/\/Dokumente\/[^/]+\/([^/]+)\//i);
  if (documentMatch) return 'RIS-Dokument';
  const eliParagraph = parsed.pathname.match(/\/eli\/.*\/(P\d+[a-z]?)\//i);
  if (eliParagraph) return `RIS: § ${eliParagraph[1].slice(1)}`;
  return 'RIS';
}

/**
 * Fremde Adresse, die als Verweis gezeigt werden darf: nur verschlüsselt
 * übertragen, ohne Zugangsdaten und ohne eigenen Port (safeHttpUrl).
 */
export function externalLinkUrl(value: unknown): string | null {
  const href = safeHttpUrl(value);
  return href !== null && new URL(href).protocol === 'https:' ? href : null;
}

export function readableSourceLinkLabel(label: string, href: string, destinationRis: string | null = null): string {
  if (destinationRis) {
    return safeHttpUrl(label) ? risReferenceLabel(destinationRis) : label;
  }
  // A URL-shaped label can otherwise pretend an external link points to RIS.
  // Keep authored prose labels such as "WKO", but expose the real host when
  // the label itself is a URL.
  if (safeHttpUrl(label)) return new URL(href).hostname;
  return label;
}

/** Gerichtskürzel, mit denen eine Fundstelle beginnen darf. */
const COURT_TOKEN = 'OGH|VwGH|VfGH|BVwG|LVwG|OLG|LG|BG|EuGH|VwG';

/**
 * Ein einzelner Ortsname nach dem Gerichtskürzel: "OLG Wien", "LG Innsbruck".
 * Genau ein großgeschriebenes Wort -- ein Verb der Antwort ist kleingeschrieben
 * und kommt damit weiterhin nicht in die Beschriftung.
 */
const COURT_PLACE = '(?:[ \\t]+[A-ZÄÖÜ][a-zäöüß]+)?';

/** Monatsnamen einer ausgeschriebenen Datumsangabe. */
const MONTH_NAME = 'Jänner|Januar|Februar|März|April|Mai|Juni|Juli|August|September'
  + '|Oktober|November|Dezember';

/** "1. Jänner 2024" oder "1.1.2024". */
const CITATION_DATE = `\\d{1,2}\\.[ \\t]*(?:(?:${MONTH_NAME})[ \\t]+\\d{4}|\\d{1,2}\\.[ \\t]*\\d{2,4})`;

/**
 * Wörter, die zwischen Gericht und Zahl stehen dürfen: eine Datumsangabe und
 * die Anbindung der Zahl. Mehr nicht -- jedes weitere zugelassene Wort zieht
 * Fließtext in die Beschriftung ("OGH befasste sich in der Entscheidung
 * RS0028818" trug zuvor den halben Satz als Namen des Verweises).
 */
const CITATION_CONNECTOR = `(?:[ \\t]+(?:vom|am)[ \\t]+${CITATION_DATE})?`
  + '(?:[ \\t]+(?:zu|zur|zum))?'
  + '(?:[ \\t]+(?:der|dem|den|die|das))?'
  + '(?:[ \\t]+(?:Zahl|Geschäftszahl|GZ))?';

/** Geschäftszahl mit führender Zahl: "8 ObS 5/23b", "9 ObA 1/24x", "12 Cg 34/22b". */
const CASE_NUMBER_NUMERIC = '\\d{1,4}[ \\t]?[A-Za-z]{1,4}[ \\t]?\\d{1,5}\\/\\d{1,4}[a-z]?';

/**
 * Geschäftszahl mit führendem Buchstaben: "Ra 2024/13/0001", "Ro 2019/19/0006",
 * "E 2802/2021", "G 123/2020". Sie gilt nur hinter einem Gerichtskürzel -- ohne
 * eines verschlänge sie sonst den Teil und die Nummer eines
 * Bundesgesetzblattes ("BGBl I 100/2023").
 */
const CASE_NUMBER_LETTER = '[A-Z][A-Za-z]{0,2}[ \\t]?\\d{1,4}\\/\\d{1,4}(?:\\/\\d{1,4})?[a-z]?';

/** Senatszahl des BVwG: "W123 2156789-1", "W123 2234567-1/5E". */
const CASE_NUMBER_SENAT = '[A-Z]\\d{2,4}[ \\t]+\\d{5,9}-\\d{1,3}(?:\\/\\d{1,3}[A-Z]?)?';

/**
 * Zahl eines Landesverwaltungsgerichts: "LVwG-2018/26/058-17",
 * "LVwG-AV-286/001-2021". Sie trägt das Gericht selbst und steht deshalb auch
 * ohne vorangestelltes Kürzel.
 */
const CASE_NUMBER_LVWG = 'LVwG-(?:[A-Z]{1,3}-)?\\d{1,4}\\/\\d{1,4}(?:\\/\\d{1,4})?-\\d{1,4}';

/** Rechtssache des EuGH: "C-311/18". */
const CASE_NUMBER_EUGH = 'C-\\d{1,4}\\/\\d{2,4}';

/** Rechtssatznummer, wie sie das RIS vergibt. */
const RECHTSSATZ_NUMBER = 'RS\\d{6,8}';

/** Zahlen, die hinter einem Gerichtskürzel stehen dürfen. */
const CITATION_AFTER_COURT = `(?:${CASE_NUMBER_SENAT}|${CASE_NUMBER_LVWG}|${CASE_NUMBER_EUGH}`
  + `|${CASE_NUMBER_NUMERIC}|${CASE_NUMBER_LETTER}|${RECHTSSATZ_NUMBER})`;

/** Zahlen, die für sich allein eine Fundstelle benennen. */
const CITATION_WITHOUT_COURT =
  `(?:${CASE_NUMBER_LVWG}|${CASE_NUMBER_NUMERIC}|${RECHTSSATZ_NUMBER})`;

/**
 * Fundstellen, die die Antwort vor der Adresse benennt: Rechtssätze und
 * Entscheidungen der Gerichte. Ohne sie stünde die Entscheidung als bloßer
 * Text da und daneben ein Verweis namens "RIS-Dokument". Die Beschriftung ist
 * ausschließlich die Fundstelle selbst: ein Gerichtskürzel, eine Datumsangabe
 * und die Zahl. Passt der Text vor der Adresse nicht auf diese Form, trägt der
 * Verweis die abgeleitete Beschriftung und der Fließtext bleibt Fließtext.
 */
const DECISION_REFERENCE_BEFORE_URL = new RegExp(
  '(\\b(?:'
    + `(?:${COURT_TOKEN})\\b${COURT_PLACE}${CITATION_CONNECTOR}[ \\t]+${CITATION_AFTER_COURT}`
    + `|${CITATION_WITHOUT_COURT}`
    + '))[ \\t]*[[(](https?:\\/\\/[^\\s\\])]+)[\\])]',
  'g',
);

/**
 * Bundesgesetzblätter nennt die Antwort ohne Adresse. Der Verweis führt auf
 * die amtliche Abfrage, wie es die frühere Chat-Formatierung tat.
 */
const BGBL_REFERENCE = /BGBl\.?\s+(I{1,3})\s+(?:Nr\.?\s+)?(\d{1,4})\/(\d{4})/g;
const BGBL_TEIL: Record<string, string> = { I: '1', II: '2', III: '3' };

/**
 * Zahl eines Gliedes, auch mit angehängtem Buchstaben: "1", "1a", "1 a". Der
 * Buchstabe zählt nur, wenn ihm kein weiterer folgt -- sonst nähme "Abs 1 lit b"
 * das "l" der lit-Angabe als Buchstaben der Zahl.
 */
const SUBDIVISION_NUMBER = '\\d{1,4}(?:[ \\t]?[a-z](?![\\wÄÖÜäöüß]))?';

/** Glieder, die eine Zahl tragen. */
const NUMBERED_SUBDIVISION = '(?:Abs|Z|Halbsatz|Satz|Nr)\\.?[ \\t]*';

/**
 * Ein Glied der Fundstelle. "lit" trägt statt der Zahl einen Buchstaben. Die
 * Reihenfolge der Glieder ist freigestellt: "Z 3 lit b" und "lit b Z 3"
 * benennen dieselbe Stelle.
 */
const SUBDIVISION = `(?:${NUMBERED_SUBDIVISION}${SUBDIVISION_NUMBER}`
  + '|lit\\.?[ \\t]*[a-z](?![\\wÄÖÜäöüß]))';

/**
 * Weitere Zahlen desselben Gliedes: "Abs 1 und Abs 2", "Abs 1, 2 und 3",
 * "Z 1 bis 3". Der Name des Gliedes darf dabei wegfallen.
 */
const SUBDIVISION_CONTINUATION = '(?:[ \\t]*,[ \\t]*|[ \\t]+(?:und|bis)[ \\t]+)'
  + `(?:${NUMBERED_SUBDIVISION})?${SUBDIVISION_NUMBER}`;

/**
 * Die Glieder hinter der Zahl des Paragraphen. Die Wiederholungen sind
 * begrenzt: unbegrenzt ineinander geschachtelt, trüge eine lange Aufzählung
 * den Ausdruck in ein Vielfaches an Versuchen.
 */
const SUBDIVISIONS = `(?:[ \\t]+${SUBDIVISION}(?:${SUBDIVISION_CONTINUATION}){0,8}){0,4}`;

/**
 * Fassungsangabe hinter der Fundstelle: "idF BGBl I 100/2023". Sie gehört
 * nicht in die Beschriftung -- der Verweis trägt die Fundstelle, die Angabe
 * bleibt daneben stehender Text.
 */
const AMENDMENT_SUFFIX = '((?:[ \\t]+i\\.?[ \\t]?d\\.?[ \\t]?F\\.?(?![\\wÄÖÜäöüß])'
  + '[^()\\[\\]\\n]{0,120}?)?)';

/**
 * Paragraphen und Artikel, die die Antwort vor der Adresse nennt. Das
 * abschließende Gesetzeskürzel bleibt freigestellt: nennt die Antwort nur
 * "§ 20 Abs 2", ist das die sichtbare Fundstelle und soll auch die
 * Beschriftung sein statt der abgeleiteten Ersatzbeschriftung. Die Glieder
 * gehören dazu: "§ 1 Abs 1 und Abs 2 KSchG" und "§ 29 Abs 1 Z 3 lit b MRG"
 * blieben sonst als halbe Fundstelle im Fließtext stehen und daneben stünde
 * ein Verweis namens "RIS-Dokument".
 */
const LEGAL_REFERENCE_BEFORE_URL = new RegExp(
  '((?:§|\\bArt(?:ikel)?\\.?)[ \\t]*\\d+[a-z]?'
    + SUBDIVISIONS
    + '(?:[ \\t]+[A-ZÄÖÜ][\\wÄÖÜäöüß]*(?:[.-][\\wÄÖÜäöüß]+)*)?)'
    + AMENDMENT_SUFFIX
    + '(\\.?)[ \\t]*[\\[(](https?:\\/\\/[^\\s\\])]+)[\\])]',
  'g',
);

/**
 * Normalizes the citation forms emitted by both research engines without
 * introducing raw HTML. Existing Markdown links are protected while bare or
 * parenthesized official RIS URLs are converted into named Markdown links.
 */
export function prepareRisMarkdown(content: string): string {
  const protectedLinks: string[] = [];
  const protect = (rendered: string): string => {
    const token = `\u0000RISLINK${protectedLinks.length}\u0000`;
    protectedLinks.push(rendered);
    return token;
  };
  let prepared = content.replace(DOCUMENT_MARKER, match => protect(match));
  // Unmarkierte Dokumentkennungen werden zur Marke, damit sie wie eine
  // angereicherte Fundstelle den Viewer öffnen statt als Fließtext dazustehen.
  prepared = prepared.replace(DOCUMENT_ID_WITHOUT_MARKER, (_match, id: string) => ` (dokumentId:${id})`);
  prepared = prepared.replace(MARKDOWN_LINK, (_match, label: string, rawUrl: string) => {
    const destinationRis = normalizeOfficialRisUrl(rawUrl);
    const href = destinationRis ?? safeHttpUrl(rawUrl);
    const readableLabel = href ? readableSourceLinkLabel(label, href, destinationRis) : label;
    const rendered = href ? `[${readableLabel}](${href})` : `[${readableLabel}](${rawUrl})`;
    return protect(rendered);
  });

  prepared = prepared.replace(
    LEGAL_REFERENCE_BEFORE_URL,
    (match, reference: string, amendment: string, punctuation: string, rawUrl: string) => {
      const href = normalizeOfficialRisUrl(rawUrl);
      return href ? `${protect(`[${reference}](${href})`)}${amendment}${punctuation}` : match;
    },
  );

  prepared = prepared.replace(BGBL_REFERENCE, (match, teil: string, nummer: string, jahr: string) => {
    const href = `https://www.ris.bka.gv.at/Ergebnis.wxe?Abfrage=BgblAuth&Bgblnummer=${encodeURIComponent(`${nummer}/${jahr}`)}&BgblTeil=${BGBL_TEIL[teil] ?? '1'}`;
    return protect(`[${match}](${href})`);
  });

  prepared = prepared.replace(
    DECISION_REFERENCE_BEFORE_URL,
    (match, reference: string, rawUrl: string) => {
      const href = normalizeOfficialRisUrl(rawUrl);
      return href ? protect(`[${reference}](${href})`) : match;
    },
  );

  prepared = prepared.replace(/[\[(](https?:\/\/[^\s\])]+)[\])]/g, (match, rawUrl: string) => {
    const href = normalizeOfficialRisUrl(rawUrl);
    return href ? protect(`[${risReferenceLabel(href)}](${href})`) : match;
  });

  // Blanke Adressen werden zum Verweis: RIS-Adressen mit ihrer abgeleiteten
  // Beschriftung, fremde Adressen mit ihrem Host. So steht in Abschnitt G der
  // Name der Stelle statt einer rohen Adresse.
  prepared = prepared.replace(HTTP_URL, (rawUrl: string) => {
    const trimmed = rawUrl.replace(/[\]),.;!?]+$/g, '');
    const suffix = rawUrl.slice(trimmed.length);
    const risHref = normalizeOfficialRisUrl(trimmed);
    if (risHref !== null) return protect(`[${risReferenceLabel(risHref)}](${risHref})${suffix}`);
    const href = externalLinkUrl(trimmed);
    return href !== null ? protect(`[${new URL(href).hostname}](${href})${suffix}`) : rawUrl;
  });

  return prepared.replace(/\u0000RISLINK(\d+)\u0000/g, (_match, index: string) => protectedLinks[Number(index)] ?? '');
}

/**
 * Schlüssel, unter dem eine Adresse im Antworttext und in der Quellenliste
 * dieselbe ist: amtliche RIS-Schreibweise (.html statt .xml, ohne Anker) und
 * Abfrageteile in fester Reihenfolge.
 */
export function sourceLabelKey(value: unknown): string | null {
  const href = normalizeOfficialRisUrl(value) ?? safeHttpUrl(value);
  if (!href) return null;
  const parsed = new URL(href);
  parsed.searchParams.sort();
  return parsed.href;
}

/**
 * Beschriftungen, die der Antworttext für seine Adressen verwendet. Die
 * Quellenliste nennt damit dieselbe Fundstelle wie die Antwort ("§ 407 UGB")
 * statt der aus der Adresse abgeleiteten Ersatzbeschriftung ("RIS-Dokument").
 * Eine bloß abgeleitete Beschriftung wird nicht aufgenommen; sie kann die
 * Quellenliste selbst bilden.
 */
export function answerLinkLabels(content: unknown): Map<string, string> {
  const labels = new Map<string, string>();
  if (typeof content !== 'string' || content.length === 0) return labels;
  const links = new RegExp(MARKDOWN_LINK.source, 'g');
  for (const match of prepareRisMarkdown(content).matchAll(links)) {
    const key = sourceLabelKey(match[2]);
    const label = match[1].trim();
    if (!key || label.length === 0 || labels.has(key)) continue;
    const risUrl = normalizeOfficialRisUrl(match[2]);
    if (risUrl !== null && label === risReferenceLabel(risUrl)) continue;
    labels.set(key, label);
  }
  return labels;
}

/** Eine im Antworttext zu benennende Fundstelle: Text und Ziel. */
export interface RisTextReference {
  text: string;
  url: string;
}

const escapeForRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Setzt die vom ToolUse-Stack gemeldeten Geschäftszahlen und Paragraphen als
 * benannte Verweise in den Antworttext. Bestehende Verweise und Dokumentmarken
 * werden währenddessen geschützt, damit keine Beschriftung ein zweites Mal
 * verlinkt wird.
 */
export function linkRisReferencesMarkdown(
  content: string,
  references: readonly RisTextReference[],
): string {
  if (references.length === 0 || content.length === 0) return content;

  const protectedLinks: string[] = [];
  const protect = (rendered: string): string => {
    const token = `\u0000RISREF${protectedLinks.length}\u0000`;
    protectedLinks.push(rendered);
    return token;
  };

  let prepared = content.replace(DOCUMENT_MARKER, match => protect(match));
  prepared = prepared.replace(MARKDOWN_LINK, match => protect(match));
  for (const reference of references) {
    const text = reference.text.trim();
    const href = normalizeOfficialRisUrl(reference.url) ?? safeHttpUrl(reference.url);
    if (text.length === 0 || href === null) continue;
    prepared = prepared.replace(
      new RegExp(`(?<!\\w)${escapeForRegExp(text)}(?!\\w)`, 'g'),
      match => protect(`[${match}](${href})`),
    );
  }

  return prepared.replace(/\u0000RISREF(\d+)\u0000/g, (_match, index: string) => protectedLinks[Number(index)] ?? '');
}

export interface ViewerRequestToken {
  sequence: number;
  controller: AbortController;
}

/** Ensures only the latest viewer request may update the modal. */
export class ViewerRequestGuard {
  private sequence = 0;
  private controller: AbortController | null = null;

  begin(): ViewerRequestToken {
    this.controller?.abort();
    const controller = new AbortController();
    const token = { sequence: ++this.sequence, controller };
    this.controller = controller;
    return token;
  }

  isCurrent(token: ViewerRequestToken): boolean {
    return token.sequence === this.sequence
      && token.controller === this.controller
      && !token.controller.signal.aborted;
  }

  cancel(): void {
    this.sequence += 1;
    this.controller?.abort();
    this.controller = null;
  }
}
